import type * as THREE from "three";
import { resolveFloorJoistLayout } from "../domain/geometry/floorJoistLayout";
import { addBox } from "./geometry";
import { matAsphaltBoard, matInsulation, matWood } from "./materials";

export { resolveFloorJoistLayout };

/**
 * Trossbotten (the floor-joist bay build-up) for the two rectangular engines.
 *
 * Facit-stacken for a bastustuga on plintgrund, top-down (TräGuiden/Svenskt
 * Trä, Hunton, Tylö), expressed in the LOCAL floor frame where the joist
 * UNDERSIDE is y = 0 and the joist top is y = joistHeight:
 *
 *   golvspont (20 mm i modellen — `ctx.pT`; verklig spont är 22 mm)
 *                             joistHeight → joistHeight + pT   (buildFloorJoists)
 *   golvbjälkar 45×145        0 → joistHeight        (buildFloorJoists)
 *   mineralull i facken       0.040 → joistHeight − 0.001
 *   trossbottenskiva 12 mm    0.028 → 0.040          asfaboard
 *   trossbottenläkt 28×70     0.000 → 0.028          spikad mot bjälksidan
 *
 * There is deliberately NO plastic/foil layer in the floor: the bjälklag has
 * to be windtight but diffusion-open so it can dry downwards. Bastufolie
 * belongs to the wall and ceiling only.
 *
 * Everything is contained WITHIN the joist height — nothing may hang below
 * y = 0, because that is where the tryckimpregnerade bärlinor live
 * (buildFoundation draws them at [−joistHeight, 0]).
 */

/** Trossbottenskiva: 12 mm asfaboard / oljehärdad board. */
const SUBFLOOR_BOARD_T = 0.012;
/**
 * Trossbottenläkt 28×70 nailed LYING against the joist side: 70 mm of
 * horizontal bearing for the board, 28 mm of thickness that the nail passes
 * through into the joist. Its underside is flush with the joist underside.
 */
const SUBFLOOR_LEDGER_H = 0.028;
const SUBFLOOR_LEDGER_W = 0.07;
/**
 * The board is cut ~2 mm shy of the clear span on each side so it can be
 * dropped into the bay from above; it still lands 68 mm deep on each läkt.
 * (The old 5 mm-per-side inset made the board float free of the joist faces,
 * which is not how a trossbotten is fitted.)
 */
const SUBFLOOR_BOARD_SIDE_GAP = 0.002;
/**
 * Mineral wool fills the remaining bjälklagshöjd — full fill is the standard
 * detail; a deliberate air gap inside the bay would only invite convection.
 * The 1 mm at the top is render clearance so the batt's top face is not
 * coplanar with the golvspont underside (z-fighting), not a design air gap.
 */
const SUBFLOOR_INSULATION_FACE_CLEARANCE = 0.001;

/**
 * Standard stone-wool batt thicknesses (mm) as sold by ISOVER/PAROC/ROCKWOOL.
 * The floor bay is NOT the joist height: the trossbottenskiva and läkt eat
 * into it from below, so a 145 mm joist leaves only a 104 mm bay.
 */
const STONE_WOOL_BATT_THICKNESSES_MM = [45, 70, 95, 120, 145, 170, 195, 220];

/**
 * Clear insulation depth (m) of a trossbotten bay — what remains of the joist
 * height above the läkt + skiva, minus the render clearance under the spont.
 * This is the exact height addSubfloorBays draws the batts at.
 */
export function resolveSubfloorInsulationDepthM(joistHeight: number): number {
	return (
		joistHeight -
		SUBFLOOR_LEDGER_H -
		SUBFLOOR_BOARD_T -
		SUBFLOOR_INSULATION_FACE_CLEARANCE
	);
}

/**
 * Thickest standard stone-wool batt that fits the trossbotten bay WITHOUT
 * compression. The BOM must quote this — not the joist height — or the
 * builder buys 145 mm batts for a 104 mm bay.
 */
export function resolveSubfloorBattThicknessMm(joistHeight: number): number {
	const depthMm = resolveSubfloorInsulationDepthM(joistHeight) * 1000;
	const fitting = STONE_WOOL_BATT_THICKNESSES_MM.filter(
		(thickness) => thickness <= depthMm,
	);
	return fitting.length > 0 ? fitting[fitting.length - 1] : 0;
}

/** Number of trossbotten bays (one per gap between adjacent floor joists). */
export function resolveSubfloorBayCount(
	width: number,
	joistWidth: number,
	cc: number,
) {
	return Math.max(
		0,
		resolveFloorJoistLayout(width, joistWidth, cc).numFloorJoists - 1,
	);
}

/**
 * Running metres of trossbottenläkt: one läkt on EACH joist side of every bay,
 * each running the full building length.
 */
export function resolveSubfloorLedgerLm(
	width: number,
	length: number,
	joistWidth: number,
	cc: number,
) {
	return 2 * resolveSubfloorBayCount(width, joistWidth, cc) * length;
}

export function addSubfloorBays({
	width,
	length,
	joistWidth,
	joistHeight,
	cc,
	group,
	namePrefix,
}: {
	width: number;
	length: number;
	joistWidth: number;
	joistHeight: number;
	cc: number;
	group: THREE.Group;
	namePrefix: string;
}) {
	const { joistPositions } = resolveFloorJoistLayout(width, joistWidth, cc);

	// buildFloorJoists puts the joist UNDERSIDE at y = 0 (joist centre at
	// joistHeight / 2). The previous version assumed joists centred on y = 0,
	// so its clamp never fired and the läkt hung 12 mm below the joists,
	// straight through the bärlinor.
	const joistBottomY = 0;
	const ledgerY = joistBottomY + SUBFLOOR_LEDGER_H / 2;
	const boardY = joistBottomY + SUBFLOOR_LEDGER_H + SUBFLOOR_BOARD_T / 2;
	const insulationBottomY = joistBottomY + SUBFLOOR_LEDGER_H + SUBFLOOR_BOARD_T;
	// Shared with the BOM's batt-thickness pick — resolveSubfloorBattThicknessMm
	// selects the thickest standard batt that fits this exact depth.
	const insulationH = resolveSubfloorInsulationDepthM(joistHeight);
	const insulationTopY = insulationBottomY + insulationH;

	for (let index = 0; index < joistPositions.length - 1; index += 1) {
		const leftJoistX = joistPositions[index];
		const rightJoistX = joistPositions[index + 1];
		const leftInnerX = leftJoistX + joistWidth / 2;
		const rightInnerX = rightJoistX - joistWidth / 2;
		const clearSpan = rightInnerX - leftInnerX;
		const bayWidth = clearSpan - 2 * SUBFLOOR_BOARD_SIDE_GAP;
		if (bayWidth <= 0) continue;

		const bayCenterX = (leftInnerX + rightInnerX) / 2;
		const bay = addBox(
			bayWidth,
			SUBFLOOR_BOARD_T,
			length,
			matAsphaltBoard,
			bayCenterX,
			boardY,
			0,
			group,
		);
		bay.name = `detail/subfloor/${namePrefix}-bay-${index + 1}`;

		if (insulationH > 0.01) {
			const insulation = addBox(
				bayWidth,
				insulationH,
				length,
				matInsulation,
				bayCenterX,
				(insulationBottomY + insulationTopY) / 2,
				0,
				group,
			);
			insulation.name = `detail/insulation/${namePrefix}-floor-${index + 1}`;
		}

		const leftLedger = addBox(
			SUBFLOOR_LEDGER_W,
			SUBFLOOR_LEDGER_H,
			length,
			matWood,
			leftInnerX + SUBFLOOR_LEDGER_W / 2,
			ledgerY,
			0,
			group,
		);
		leftLedger.name = `detail/subfloor-ledger/${namePrefix}-${index + 1}-left`;

		const rightLedger = addBox(
			SUBFLOOR_LEDGER_W,
			SUBFLOOR_LEDGER_H,
			length,
			matWood,
			rightInnerX - SUBFLOOR_LEDGER_W / 2,
			ledgerY,
			0,
			group,
		);
		rightLedger.name = `detail/subfloor-ledger/${namePrefix}-${index + 1}-right`;
	}
}
